import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Product } from '@app/core/business/product/product';
import { ProductService } from '@app/core/business/product/product.service';
import { ContextService, FullContext } from '@app/core/service/context.service';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { OptionItem } from '../autocomplete/autocomplete.component';
import { environment } from '@env/environment';
import { Router } from '@angular/router';
import { OfferStateService } from '../offer/offer-state.service';
import { untilDestroyed } from 'ngx-take-until-destroy';


/***
 * Composant de sélection de produit avec autocomplétion
 * - fetchProducts(term) => Observable<OptionItem<Product>[]> : fonction de récupération des produits
 * - displayProduct(product) => string : fonction de rendu du libellé produit
 * - (selected) : émis lors de la sélection d’un produit
 * - (enterTerm) : émis lors de la validation d’un terme (touche Entrée)
 * - navigateToProduct(product) : navigation vers la fiche produit
 * - navigateToSearch(term) : navigation vers la page d’offres avec le terme de recherche
 * Exemples d’utilisation :
 * <app-product-picker (selected)="onProductSelected($event)"></app-product-picker>
 * <app-product-picker (enterTerm)="onEnterTerm($event)"></app-product-picker>
 */
@Component({
  selector: 'app-product-picker',
  templateUrl: './product-picker.component.html',
  styleUrls: ['./product-picker.component.scss'],

})
export class ProductPickerComponent implements OnInit, OnDestroy {
  readonly label = '';
  readonly placeholder = 'Rechercher un produit…';
  readonly minLength = 3;
  readonly debounceMs = 300;
  readonly numberMaxProducts = 10;
  public fullContext: FullContext = null;


  constructor(private readonly productService: ProductService, private readonly contextService: ContextService,
    private router: Router, private offerStateService: OfferStateService
  ) { }
  ngOnDestroy(): void {
  }


  ngOnInit(): void {
    this.contextService.fullContext$
      .pipe(untilDestroyed(this))
      .subscribe(async fullContext => {
        this.fullContext = fullContext;
      })
  }

  /**
   * Récupération des produits via le service ProductService
   * @param term 
   * @returns 
   */
  public fetchProducts = (term: string): Observable<OptionItem<Product>[]> => {
    return this.productService
      .getProductSearch(
        this.contextService.context.companyCode,
        term,
        false,
        this.contextService.context.customerCode,
        this.contextService.context.orderCode,
        10
      )
      .pipe(
        map(products => (products ?? []).map(p => {
          let parts: string[] = [];
          if (this.fullContext.company.code === "PRONATURAFRANCE") {
            // Label toujours affiché
            parts.push(p.label);
          } else {
            // Code + label
            parts.push(p.code, p.label);
          }
          // Brand uniquement si dispo
          if (p.brand) {
            parts.push(p.brand);
          }

          const t = parts.filter(x => !!x).join(" - ");

          return {
            text: t,
            img: p?.pictures?.[0]?.url ? environment.baseFileUrl + p.pictures[0].url : null,
            picto: p.pictos?.[0] ?? null,
            data: p,
            weight: p.weight
          } as OptionItem<Product>;
        })),
        catchError(() => of([])) // En cas d'erreur, on retourne une liste vide
      );
  };

  /** Rendu du libellé produit (métier) */
  displayProduct = (p: Product) => p?.label ?? p?.code ?? '';



  /** Relay vers l’extérieur (métier) FICHE PRODUIT */
  onProductSelected(p: Product | any) {
    if (p && p != null) {
      this.router.navigate(['/product', p?.code]);
    }
  }

  /** Relay vers l’extérieur (métier) RECHERCHE */
  onEnterTerm(term: string) {
    if (term && term.length !== 0) {
      this.router.navigate(['/offers', 'search', term]);
    }
    this.offerStateService.setMode(this.fullContext.company?.settings?.offerDefaultView);
  }

}
